import { create } from 'zustand'
import { LiveViewObject } from '../DeviceConfig/utils'
import { getSupportedFunctionalitiesRequest, setZoomAndFocusValuesRequest } from './api/requests'

interface LiveViewState {
    brightness: number
    ranges: {
        zoom: { min: number; max: number; step: number }
        focus: { min: number; max: number; step: number }
        brightness: { min: number; max: number; step: number }
    }
}

export interface SupportedFunctionalities {
    regional_autofocus: boolean
    autofocus: boolean
    zoom_focus: boolean
    snapshot: boolean
    live_view: boolean
    brightness: boolean
    black_mask: {
        supported: boolean
        shape_types: string[]
        max_polygon_points: number
        max_masks: number
    }
}

interface LiveViewStateStore {
    areaFocusEnabled: boolean
    stationName: string | null
    deviceName: string | null
    streamStarted: Record<string, boolean>
    blackmaskEnabled: boolean
    liveViewObject: LiveViewObject | null
    isPmView: boolean
    errorMessage: string | null
    initialLoadInitiated: boolean
    liveView: LiveViewState
    selectedTab: 'focusZoom' | 'brightness' | 'blackMask'
    baseURL: string | null
    idle: boolean
    fullscreen: boolean
    supported_functionalities: SupportedFunctionalities | null
    loadingZoomFocus: boolean
    loadingBrightness: boolean
    setRange: (rangeType: keyof LiveViewState['ranges'], min: number, max: number, step: number) => void
    setFullscreen: (fullscreen: boolean) => void
    setBrightness: (brightness: number) => void
    setSupportedFunctionalities: (supported_functionalities: SupportedFunctionalities | null) => void
    setIsIdle: (idle: boolean) => void
    setSelectedTab: (selectedTab: 'focusZoom' | 'brightness' | 'blackMask') => void
    setLoadingZoomFocus: (loadingZoomFocus: boolean) => void
    setLoadingBrightness: (loadingBrightness: boolean) => void
    setBaseURL: (baseURL: string) => void
    setErrorMessage: (errorMessage: string | null) => void
    setIsPmView: (isPmView: boolean) => void
    setLiveViewObject: (liveViewObject: LiveViewObject | null) => void
    setBlackmaskEnabled: (blackmaskEnabled: boolean) => void
    setStreamStarted: (baseURL: string, streamStarted: boolean) => void
    setStationName: (stationName: string | null) => void
    setDeviceName: (deviceName: string | null) => void
    transferZoomAndFocusToLiveView: (values: { zoom?: number; focus?: number; autofocus?: boolean }) => Promise<void>
    setAreaFocusEnabled: (areaFocusEnabled: boolean) => void
    checkDirtyLens: boolean
    setCheckDirtyLens: (checkDirtyLens: boolean) => void
    hdMode: boolean
    setHdMode: (hdMode: boolean) => void
    hdLoading: boolean
    setHdLoading: (hdLoading: boolean) => void
}

export const useLiveViewStateStore = create<LiveViewStateStore>((set, get) => ({
    areaFocusEnabled: false,
    stationName: null,
    deviceName: null,
    streamStarted: {},
    blackmaskEnabled: false,
    liveViewObject: null,
    isPmView: false,
    errorMessage: null,
    initialLoadInitiated: false,
    liveView: {
        brightness: 0,
        ranges: {
            zoom: {
                min: 0,
                max: 100,
                step: 1
            },
            focus: {
                min: 0,
                max: 100,
                step: 1
            },
            brightness: {
                min: 0,
                max: 100,
                step: 1
            }
        }
    },
    selectedTab: 'focusZoom',
    loadingZoomFocus: false,
    loadingBrightness: false,
    baseURL: null,
    idle: true,
    fullscreen: false,
    supported_functionalities: {
        regional_autofocus: true,
        autofocus: true,
        zoom_focus: true,
        snapshot: true,
        live_view: true,
        brightness: true,
        black_mask: {
            supported: true,
            shape_types: ['polygon'],
            max_polygon_points: 4,
            max_masks: 8
        }
    },
    checkDirtyLens: false,
    hdMode: false,
    hdLoading: false,

    setRange: (rangeType: keyof LiveViewState['ranges'], min: number, max: number, step: number) =>
        set((state) => ({
            liveView: {
                ...state.liveView,
                ranges: {
                    ...state.liveView.ranges,
                    [rangeType]: { min, max, step }
                }
            }
        })),

    setFullscreen: (fullscreen) =>
        set((state) => ({
            fullscreen
        })),

    setSupportedFunctionalities: (supported_functionalities) => {
        set((state) => ({
            supported_functionalities
        }))
    },
    setIsIdle: (idle) => {
        set((state) => ({
            idle
        }))
    },
    setSelectedTab: (selectedTab) => {
        set((state) => ({
            selectedTab
        }))
    },
    setBrightness: (brightness) => {
        set((state) => ({
            liveView: { ...state.liveView, brightness }
        }))
    },
    setBaseURL: (baseURL) => {
        set((state) => ({
            baseURL
        }))
    },
    setLoadingZoomFocus: (loadingZoomFocus) => {
        set((state) => ({
            loadingZoomFocus
        }))
    },
    setLoadingBrightness: (loadingBrightness) => {
        set((state) => ({
            loadingBrightness
        }))
    },
    setErrorMessage: (errorMessage) => {
        set((state) => ({
            errorMessage
        }))
    },
    setIsPmView: (isPmView) => {
        set((state) => ({
            isPmView
        }))
    },
    setLiveViewObject: (liveViewObject: LiveViewObject | null) => {
        set((state) => ({
            liveViewObject
        }))
    },
    setBlackmaskEnabled: (blackmaskEnabled) => {
        set((state) => ({
            blackmaskEnabled
        }))
    },
    setStreamStarted: (baseURL: string, streamStarted: boolean) => {
        set((state) => ({
            streamStarted: { ...state.streamStarted, [baseURL]: streamStarted },
            ...(streamStarted ? {} : { hdMode: false, hdLoading: false })
        }))
    },
    setStationName: (stationName) => {
        set((state) => ({
            stationName
        }))
    },
    setDeviceName: (deviceName) => {
        set((state) => ({
            deviceName
        }))
    },
    transferZoomAndFocusToLiveView: async (values: { zoom?: number; focus?: number; autofocus?: boolean }) => {
        const { baseURL } = get()
        set({ loadingZoomFocus: true })

        try {
            await setZoomAndFocusValuesRequest(baseURL, { ...values })
        } catch (error) {
        } finally {
            set({ loadingZoomFocus: false })
        }
    },
    setAreaFocusEnabled: (areaFocusEnabled) => {
        set((state) => ({
            areaFocusEnabled
        }))
    },
    setCheckDirtyLens: (checkDirtyLens) => {
        set((state) => ({
            checkDirtyLens: checkDirtyLens
        }))
    },
    setHdMode: (hdMode) => {
        set(() => ({ hdMode }))
    },
    setHdLoading: (hdLoading) => {
        set(() => ({ hdLoading }))
    }
}))

// New separate store for zoom and focus values
interface ZoomFocusState {
    zoom: number
    focus: number
    setZoom: (zoom: number) => void
    setFocus: (focus: number) => void
}

export const useZoomFocusStore = create<ZoomFocusState>((set) => ({
    zoom: 0,
    focus: 0,
    setZoom: (zoom: number) => {
        set((state) => {
            sessionStorage.setItem('zoom', zoom.toString())
            if (state.zoom !== zoom) {
                return { zoom }
            }
            return state
        })
    },
    setFocus: (focus: number) => {
        set((state) => {
            sessionStorage.setItem('focus', focus.toString())
            if (state.focus !== focus) {
                return { focus }
            }
            return state
        })
    }
}))
